import type { estypes } from '@elastic/elasticsearch'
import type { ParsedEvent, ReconnectInterval } from 'eventsource-parser'

import type { NonArray, SearchObjectNoteFragment } from './mapping'
import {
  type AgentTaggedEventFields,
  type ESParams,
  isSupportedSuperToolChainEventId,
  type SuperStreamEventArtifacts,
  type SuperStreamEventBuildingAnalysis,
  type SuperStreamEventFailingTool,
  type SuperStreamEventGithub,
  type SuperStreamEventSearchFilters,
  type SuperStreamEventSourceExplanation,
  type SuperStreamEventSources,
  type SuperStreamEventTokenConsumption,
  type SuperStreamEventToolExplanationText,
  type SuperStreamToolParameters,
} from './super'

export enum AskStreamEventId {
  Start = 'start',
  End = 'end',

  ThreadTitle = 'thread-title',
  TokenConsumption = 'token-consumption',
  SuperGithub = 'askx-github',
  SuperSourceExplanation = 'askx-source-explanation',
  SuperSources = 'askx-sources',
  SuperToolExplanationText = 'askx-tool-explanation',
  SuperArtifacts = 'super-artifacts',
  ThreadId = 'thread-id',
  Text = 'text',
  AnswerTruncated = 'answer-truncated',
  /** Sent again as `text` or `thought` once it settles. */
  AnswerDraft = 'answer-draft',
  AnswerDraftEnd = 'answer-draft-end',
  Thought = 'thought',
  Error = 'ask-error',
  NoAnswer = 'no-answer',
  BadAnswer = 'bad-answer',
  DebugSuperQuery = 'debug-askx-query',
  InferredPrecision = 'inferred-precision',
  FollowUpQuestions = 'follow-up-questions',
  SearchFilters = 'search-filters',
  BuildingAnalysis = 'building-analysis',
  Overloaded = 'overloaded',
  FailingTool = 'failing-tool',
  ToolParameters = 'tool-parameters',
}

export enum AnswerDraftOutcome {
  Answer = 'answer',
  Narration = 'narration',
  Dropped = 'dropped',
}

export type AskStreamEventText = {
  text: string
}

type AskRequiredFields = Required<{
  [K in keyof Pick<
    SearchObjectNoteFragment,
    | 'fragmentFullText'
    | 'fullPath'
    | 'noteId'
    | 'title'
    | 'fragmentTitle'
    | 'fragmentSemantic'
    | 'fragmentMarkdown'
    | 'fragmentId'
    | 'type'
    | 'fragmentRange'
    | 'updatedAt'
    | 'lastEditedAt'
    | 'channelPurpose'
    | 'fragmentDatabase'
    | 'fragmentVectorBGE'
    | 'createdAt'
    | 'fragmentIndex'
  >]: NonNullable<NonArray<SearchObjectNoteFragment[K]>>
}> & {
  [K in keyof Pick<
    SearchObjectNoteFragment,
    'iconShape' | 'iconColor' | 'iconVariant' | 'reviewState' | 'fragmentType'
  >]: NonNullable<NonArray<SearchObjectNoteFragment[K]>>
} & Required<{
    [K in keyof Pick<
      SearchObjectNoteFragment,
      'parentNotesIdList' | 'parentNotesTitleList'
    >]: NonNullable<NonArray<SearchObjectNoteFragment[K]>>[]
  }>

export type AskSource = {
  score: number
  scoreBeforeDecay: number
  answerSimilarityScore: number
  explanation?: estypes.ExplainExplanation
  fragmentRange?: [number[], number[]]
  parentNotes: { id: string; title: string }[]
} & Omit<
  AskRequiredFields,
  | 'fragmentFullText'
  | 'fragmentMarkdown'
  | 'fragmentDatabase'
  | 'fragmentSemantic'
  | 'fragmentRange'
  | 'parentNotesTitleList'
  | 'parentNotesIdList'
  | 'fragmentVectorBGE'
> &
  Partial<
    Pick<
      AskRequiredFields,
      | 'fragmentFullText'
      | 'fragmentMarkdown'
      | 'fragmentSemantic'
      | 'fragmentType'
      | 'fragmentVectorBGE'
    >
  >

export type AskStreamStartNote = {
  id: string
  title: string | null
  /** Legacy-only: the agent-kernel route sends no icons, and history replay
   *  falls back to the generic document icon. */
  iconShape?: string | null
  iconColor?: string | null
}

type AskStreamEventStart = {
  question: string
  note?: AskStreamStartNote
  /** Caller-attached context for this turn (a Slack conversation, an
   *  assistant's live context) — persisted so history keeps what shaped
   *  the answer. */
  roundContext?: string
} & AgentTaggedEventFields

type AskStreamEventThreadTitle = {
  title: string
}

// Copied from collab
enum ColumnTypes {
  COLLECTION_NOTE_TITLE = 'collection-note-title',
  CREATED_AT = 'created-at',
  CREATED_BY = 'created-by',
  DATE = 'date',
  MULTI_SELECT = 'multi-select',
  NAME = 'name',
  NOTES = 'notes',
  NUMBER = 'number',
  SELECT = 'select',
  // TODO: remove once our internal tables get off deprecated "grid"
  TODO = 'todo',
  UPDATED_AT = 'updated-at',
  UPDATED_BY = 'updated-by',
  USERS = 'users',
  LINK = 'link',
}

export type NoteDatabaseFragment = {
  blockId: string
  title: string
  range: [number[], number[]]
  columns: Record<
    string,
    { type: ColumnTypes; name: string; options?: string[] }
  >
  data: { key: string; fields: Record<string, string> }[]
}

type AskStreamEventNoAnswer = {
  /** Words the asker can search; absent when the count could not be taken. */
  totalWords?: number
}

type AskStreamEventInferredPrecision = {
  version: string
  precision: string | null
  suggestions?: string[]
}

type AskStreamEventFollowUpQuestion = {
  version: string
  questions?: string[]
}

export function isSupportedAskEventId(
  askEventId: string | undefined
): askEventId is AskStreamEventId {
  return Boolean(
    askEventId &&
      Object.values(AskStreamEventId)
        .map((a) => a.toString())
        .includes(askEventId)
  )
}

export type AskStreamEvent =
  | { id: AskStreamEventId.Start; event: AskStreamEventStart }
  | { id: AskStreamEventId.End; event: object }
  | { id: AskStreamEventId.ThreadTitle; event: AskStreamEventThreadTitle }
  | { id: AskStreamEventId.NoAnswer; event: AskStreamEventNoAnswer }
  | { id: AskStreamEventId.BadAnswer; event?: string }
  | {
      id: AskStreamEventId.InferredPrecision
      event: AskStreamEventInferredPrecision
    }
  | {
      id: AskStreamEventId.FollowUpQuestions
      event: AskStreamEventFollowUpQuestion
    }
  | { id: AskStreamEventId.Text; event: { text: string; source?: string } }
  | { id: AskStreamEventId.AnswerTruncated; event: object }
  | { id: AskStreamEventId.AnswerDraft; event: { text: string } }
  | {
      id: AskStreamEventId.AnswerDraftEnd
      event: { as: AnswerDraftOutcome }
    }
  | { id: AskStreamEventId.Thought; event: { text: string; stepId?: number } }
  | { id: AskStreamEventId.Error; event: { message?: string; name?: string } }
  // Specific to super
  | { id: AskStreamEventId.SuperSources; event: SuperStreamEventSources }
  | { id: AskStreamEventId.SuperArtifacts; event: SuperStreamEventArtifacts }
  | {
      id: AskStreamEventId.ThreadId
      event: { threadId: string; runId?: string }
    }
  | {
      id: AskStreamEventId.SuperSourceExplanation
      event: SuperStreamEventSourceExplanation
    }
  | {
      id: AskStreamEventId.SuperGithub
      event: SuperStreamEventGithub
    }
  | {
      id: AskStreamEventId.SuperToolExplanationText
      event: SuperStreamEventToolExplanationText
    }
  | {
      id: AskStreamEventId.DebugSuperQuery
      event: {
        query: ESParams
        semanticContent?: string
      }
    }
  | {
      id: AskStreamEventId.TokenConsumption
      event: SuperStreamEventTokenConsumption
    }
  | {
      id: AskStreamEventId.SearchFilters
      event: SuperStreamEventSearchFilters
    }
  | {
      id: AskStreamEventId.BuildingAnalysis
      event: SuperStreamEventBuildingAnalysis
    }
  | {
      id: AskStreamEventId.Overloaded
      event: { tries?: number; step?: string; fallback?: boolean }
    }
  | {
      id: AskStreamEventId.FailingTool
      event: SuperStreamEventFailingTool
    }
  | {
      id: AskStreamEventId.ToolParameters
      event: SuperStreamToolParameters
    }

export function handleAskEvents(
  _: AskStreamEvent | undefined,
  onData: (askStreamEvent: AskStreamEvent) => void
) {
  return function handleSseEvent(parsedEvent: ParsedEvent | ReconnectInterval) {
    if (parsedEvent.type !== 'event') {
      return
    }
    const { event: eventId, data } = parsedEvent
    if (
      !(
        isSupportedAskEventId(eventId) ||
        isSupportedSuperToolChainEventId(eventId)
      )
    ) {
      return
    }
    const event = {
      id: eventId,
      event: JSON.parse(data),
    } as AskStreamEvent //TS complaining otherwise because union has 25+ members
    onData(event)
  }
}
